import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { NavigationService } from '@treasury/core/navigation';
import { ReportListItem } from '@treasury/domain/reports/report-list-item.entity';
import { ReportingService } from '@treasury/domain/reports/reports.service';
import { TmContainer } from '@treasury/presentation';
import { TmDialogButtonPosture, TmDialogService } from '@treasury/presentation/services/dialog';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import '@treasury/presentation/components/tm-blocking-loader';
import '@treasury/presentation/components/tm-bottom-sheet';
import '@treasury/presentation/components/tm-footer';
import '@treasury/presentation/components/tm-loader';
import '@treasury/presentation/components/tm-text-field';

import { DynamicComponentExitReason } from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { ReportFiltersVm } from '../data/report-filters.vm';
import { ReportMetaDataVm } from '../data/report-meta-data.vm';
import './custom-report-form';
import './report-filters';

const tagName = 'report-save-custom';
@customElement(tagName)
export class ReportSaveCustom extends TmContainer {
    @property({ attribute: false })
    public reportFilters: ReportFiltersVm = new ReportFiltersVm();

    @property({ attribute: false })
    public reportMetaData?: ReportMetaDataVm;

    @property({ type: Boolean })
    public open = false;

    @property({ attribute: false })
    public report: ReportListItem | undefined;

    @state()
    private reportId = 0;

    @InjectProperty()
    declare private navigationService: NavigationService;

    @InjectProperty()
    declare private reportingService: ReportingService;

    @InjectProperty()
    declare private readonly dialogService: TmDialogService;

    @state()
    private showModal = screen.width >= 1024;

    @state()
    private isCustomReportFormValid = false;

    public async updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has('report') && this.report) {
            this.reportId = this.report.id;
            await this.getReport();
        }

        if (changedProperties.has('reportMetaData') && this.reportMetaData && this.reportFilters) {
            this.loading = false;
            if (this.showModal && this.open) {
                this.showDialog(this.reportMetaData);
            }
        }

        if (changedProperties.has('open')) {
            if (this.showModal && this.open && this.reportMetaData) {
                this.showDialog(this.reportMetaData);
            }
            if (!this.open) {
                this.dialogService.close();
                this.resetFilters();
            }
        }
    }

    public render() {
        if (!this.reportMetaData) {
            return nothing;
        }

        if (this.showModal) {
            // let the dialog service handle rendering out-of-band
            return nothing;
        }

        // we're on a mobile device; show the bottom sheet instead
        return html`<tm-bottom-sheet
            .open=${this.open}
            @close=${() => {
                this.close();
            }}
            ><span slot="header-center">Save Report</span>
            <div class="py-3 px-3 bg-primary">
                <custom-report-form
                    .report=${this.reportMetaData}
                    .filters=${this.reportFilters}
                    @validity-changed=${(e: CustomEvent) => {
                        this.isCustomReportFormValid = e.detail;
                    }}
                ></custom-report-form>
            </div>
            <tm-footer
                .buttonConfig=${[
                    {
                        importance: 'primary',
                        text: 'Save Report',
                        onClick: () => {
                            if (!this.reportMetaData) {
                                throw new Error('Could not save report');
                            }
                            this.saveReport(this.reportMetaData);
                        },
                        size: 'large',
                        disabled: !this.isCustomReportFormValid,
                    },
                    {
                        importance: 'secondary',
                        text: 'Cancel',
                        onClick: () => {
                            this.close();
                        },
                        size: 'large',
                    },
                ]}
            ></tm-footer
        ></tm-bottom-sheet>`;
    }

    private resetFilters() {
        this.reportFilters.reset();
        this.loading = true;
        setTimeout(() => {
            this.loading = false;
        }, 100);
    }

    private getReport() {
        return this.tryFetch(
            () =>
                Promise.all([
                    this.reportingService.getReportMetaDataAndFilters(this.reportId.toString()),
                    this.reportingService.getReportAccounts(this.reportFilters.typeRequest),
                    this.reportingService.getReportTransactionCodes(),
                ]),
            ([data, accounts, paymentTypes]) => {
                this.reportMetaData = new ReportMetaDataVm(data.metaData);
                this.reportFilters = new ReportFiltersVm(data.filters.toDto());
                this.reportFilters.accounts = accounts;
                this.reportFilters.paymentTypes = paymentTypes;
            },
            () => {
                this.notificationService.renderError('Failed to fetch report metadata');
            }
        );
    }

    private extractErrorMessage(err: object) {
        const errorString = err.toString();
        if (errorString.includes('Error:')) {
            return errorString.split('Error:')[1];
        }
        return errorString;
    }

    private async saveReport(reportMetaData: ReportMetaDataVm) {
        if (reportMetaData.isStandardReport) {
            this.tryFetch(
                () => this.reportingService.saveReport(reportMetaData.report, this.reportFilters),
                () => {
                    this.notificationService.renderSuccess('Report saved successfully');
                    this.open = false;
                    this.dispatchEvent(new CustomEvent('saved-new-report'));
                },
                err => {
                    const message = this.extractErrorMessage(err as object);
                    const markup = html`<div>Failed to save report.</div>
                        <div>${message}</div>`;
                    this.notificationService.renderError(markup);
                }
            );
        } else {
            this.tryFetch(
                () => this.reportingService.updateReport(reportMetaData.report, this.reportFilters),
                () => {
                    this.notificationService.renderSuccess('Report updated successfully');
                    this.open = false;
                    this.dispatchEvent(new CustomEvent('updated-report'));
                },
                () => {
                    this.notificationService.renderError('Failed to update report');
                }
            );
        }
    }

    private async showDialog(reportMetaData: ReportMetaDataVm) {
        if (this.dialogService.isDialogOpen) {
            return;
        }

        const { reason } = await this.dialogService.open(
            setData =>
                html`<custom-report-form
                    .report=${this.reportMetaData}
                    .filters=${this.reportFilters}
                    @validity-changed=${(e: CustomEvent) => {
                        this.isCustomReportFormValid = e.detail;
                        setData(e.detail);
                    }}
                ></custom-report-form>`,
            'Save Custom Report',
            {
                buttons: [
                    {
                        label: 'Save Report',
                        reason: DynamicComponentExitReason.Confirm,
                        posture: TmDialogButtonPosture.Primary,
                        checkDisabled: isValid => !isValid,
                    },
                    {
                        label: 'Cancel',
                        reason: DynamicComponentExitReason.Cancel,
                        posture: TmDialogButtonPosture.Secondary,
                    },
                ],
                classes: ['max-w-[800px]'],
            }
        );

        if (reason === DynamicComponentExitReason.Confirm) {
            this.saveReport(reportMetaData);
        } else {
            this.close();
        }
    }

    private close() {
        this.open = false;
        this.dispatchEvent(new CustomEvent('close'));
        this.resetFilters();
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: ReportSaveCustom;
    }
}
